/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the depot management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { RequestState, IRequestState } from '@ngt/request-utilities';

import { Button, DialogTitle, DialogContent, Dialog, DialogActions, Grid, MenuItem, FormControlLabel } from '@material-ui/core';

import CircularProgress from '@material-ui/core/CircularProgress';
import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';
import { useSnackbar } from 'notistack';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

import * as Dtos from '../../../api/Neon.Dtos';
import { AsyncForm } from '../../../components/FinalForm/AsyncForm';
import { DepotActions } from '../../../store/modules/drugmanagement/depot';
import TextFieldWrapper from '../../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import { SelectWrapper } from '../../../components/FinalForm/FinalFormControls/SelectWrapper';
import useAsyncFunction from '../../../hooks/useAsyncFunction';
import { CheckboxWrapper } from '../../../components/FinalForm/FinalFormControls/CheckboxWrapper';


/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IDepotModalProps {
    depot: Dtos.Depot | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    orderForms: Dtos.OrderForm[] | null;
    orderFormLoadState: IRequestState<Dtos.ResponseStatus>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IDepotFormProps extends FormRenderProps<Dtos.Depot> {
    orderForms: Dtos.OrderForm[] | null;
    orderFormLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

interface IDepotForm extends Partial<Dtos.Depot> {
}

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IDepotForm>({
    depotName: Yup.string().required(),
    address: Yup.string().required(),
    email: Yup.string().required(),
    contactName: Yup.string().required(),
    phoneNo: Yup.string().required(),
    orderFormId: Yup.number().required()
});

const DepotForm: React.FunctionComponent<IDepotFormProps> = ({
    orderForms,
    orderFormLoadState,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to save the depot.', { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const orderFormItems = React.useMemo(() => {
        if (!orderForms || orderForms.length === 0) {
            return [];
        }
        else {
            return orderForms.map(orderForm => {
                return <MenuItem
                    key={orderForm.id}
                    value={orderForm.id}
                >
                    {orderForm.orderFormName}
                </MenuItem>;
            });
        }
    }, [orderForms]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const orderFormDisabled = React.useMemo(() => {
        return (orderForms && orderForms.length === 1) || formDisabled;
    }, [orderForms, formDisabled]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="depotName"
                            component={TextFieldWrapper}
                            type="text"
                            label="Depot Name"
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="address"
                            component={TextFieldWrapper}
                            type="text"
                            label="Address"
                            disabled={formDisabled}
                            multiline={true}
                            rows={4}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="email"
                            component={TextFieldWrapper}
                            type="text"
                            label="Email (separate multiple addresses with a semicolon)"
                            disabled={formDisabled}
                            multiline={true}
                            rows={2}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="contactName"
                            component={TextFieldWrapper}
                            type="text"
                            label="Contact Name"
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="phoneNo"
                            component={TextFieldWrapper}
                            type="text"
                            label="Phone Number"
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {
                            orderFormLoadState && orderFormLoadState.state && orderFormLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="orderFormId"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={orderFormDisabled}
                                    label="Order Form"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {orderFormItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Field
                                    fullWidth
                                    name="active"
                                    component={CheckboxWrapper}
                                    label="Active"
                                    disabled={formDisabled}
                                />
                            }
                            label="Active"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const DepotModal: React.FunctionComponent<IDepotModalProps> = ({
    depot,
    modalOpen,
    setModalOpen,
    orderForms,
    orderFormLoadState,
    onCloseClick,
    onFormSave
}) => {
    const depotForm = React.useCallback((props: FormRenderProps<Dtos.Depot>) => {
        return (
            <DepotForm
                orderForms={orderForms}
                orderFormLoadState={orderFormLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [orderForms, orderFormLoadState, setModalOpen, onFormSave]);

    const asyncSave = useAsyncFunction(DepotActions.save, DepotActions.saveSuccess, DepotActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Depot
                </DialogTitle>
                <AsyncForm
                    initialValues={depot}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={depotForm}
                />
            </Dialog>
        </>
    );
}

export default DepotModal;